import { cn } from '@repo/tailwind-config/tailwind.functions'
import * as React from 'react'

import { typography } from '../../atoms/typography'

type ScrollSource = 'wrapper' | 'proxy'

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & {
    overflow?: boolean
    wrapperClassName?: string
    onGrabAction?: (val: boolean) => void
  }
>(({ className, overflow = false, wrapperClassName, onGrabAction, ...props }, ref) => {
  const [isDragging, setIsDragging] = React.useState(false)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const startX = React.useRef<number>(0)
  const scrollLeft = React.useRef<number>(0)
  const grabTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const dragThreshold = 200 // Duration threshold in milliseconds

  // Sticky proxy scrollbar: mirrors the wrapper's horizontal scroll so the
  // scrollbar stays reachable at the bottom of the viewport on long tables.
  const tableRef = React.useRef<HTMLTableElement>(null)
  const proxyRef = React.useRef<HTMLDivElement>(null)
  const [scrollWidth, setScrollWidth] = React.useState(0)
  const [hasOverflow, setHasOverflow] = React.useState(false)

  React.useImperativeHandle(ref, () => tableRef.current as HTMLTableElement)

  const measure = React.useCallback(() => {
    const wrapper = scrollContainerRef.current
    if (!wrapper) return

    setScrollWidth(wrapper.scrollWidth)
    setHasOverflow(wrapper.scrollWidth > wrapper.clientWidth)
  }, [])

  React.useEffect(() => {
    if (overflow) return

    measure()

    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(measure)

    if (scrollContainerRef.current) observer.observe(scrollContainerRef.current)
    if (tableRef.current) observer.observe(tableRef.current)

    return () => observer.disconnect()
  }, [measure, overflow])

  // Whichever element the user is scrolling owns the sync while it keeps
  // emitting events. The mirrored element's scroll event arrives a frame later
  // with a stale value; without this lock it would drag the source backwards.
  const scrollSourceRef = React.useRef<{ source: ScrollSource; at: number } | null>(null)
  const syncLockMs = 150

  const syncScrollLeft = (
    source: ScrollSource,
    from: HTMLDivElement | null,
    to: HTMLDivElement | null
  ) => {
    const now = Date.now()
    const current = scrollSourceRef.current

    if (current && current.source !== source && now - current.at < syncLockMs) return

    scrollSourceRef.current = { source, at: now }

    if (!from || !to || Math.abs(to.scrollLeft - from.scrollLeft) < 1) return

    to.scrollLeft = from.scrollLeft
  }

  const handleWrapperScroll = () =>
    syncScrollLeft('wrapper', scrollContainerRef.current, proxyRef.current)

  const handleProxyScroll = () =>
    syncScrollLeft('proxy', proxyRef.current, scrollContainerRef.current)

  const showProxy = !overflow && hasOverflow

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scrollContainerRef.current) {
      // Track start position for drag
      startX.current = e.pageX - scrollContainerRef.current.offsetLeft
      scrollLeft.current = scrollContainerRef.current.scrollLeft

      // Set a timeout to set grabbing true after a certain duration
      grabTimeoutRef.current = setTimeout(() => {
        setIsDragging(true)
        onGrabAction?.(true)
      }, dragThreshold)
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return
    e.preventDefault()
    e.stopPropagation()

    const x = e.pageX - scrollContainerRef.current.offsetLeft
    const walk = (x - startX.current) * 2 // Adjust scroll speed
    scrollContainerRef.current.scrollLeft = scrollLeft.current - walk
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Clear the grab timeout to prevent isGrabbing from being set
    if (grabTimeoutRef.current) {
      clearTimeout(grabTimeoutRef.current)
      grabTimeoutRef.current = null // Reset timeout reference
    }

    if (isDragging) {
      setIsDragging(false)
      if (onGrabAction) {
        setTimeout(() => {
          onGrabAction(false)
        }, 100)
      }
    }
  }

  const handleMouseLeave = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Clear the grab timeout if mouse leaves
    if (grabTimeoutRef.current) {
      clearTimeout(grabTimeoutRef.current)
      grabTimeoutRef.current = null // Reset timeout reference
    }

    if (isDragging) {
      setIsDragging(false)
      onGrabAction?.(false)
    }
  }

  return (
    <div className="w-full">
      <div
        ref={scrollContainerRef}
        onScroll={handleWrapperScroll}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        className={cn(
          'relative w-full !rounded-3xl bg-gray-50 border border-primary-light',
          wrapperClassName,
          {
            'overflow-y-visible': overflow,
            '!overflow-x-auto': !overflow,
            'scrollbar-hide': showProxy,
            'cursor-grabbing': isDragging,
            'cursor-auto': !isDragging
          }
        )}
      >
        <table
          ref={tableRef}
          className={cn('w-full caption-bottom text-sm', className)}
          {...props}
        />
      </div>

      {showProxy ? (
        <div
          ref={proxyRef}
          onScroll={handleProxyScroll}
          className="sticky bottom-0 h-2.5 w-full overflow-x-auto overflow-y-hidden"
        >
          <div style={{ width: scrollWidth, height: 1 }} />
        </div>
      ) : null}
    </div>
  )
})

Table.displayName = 'Table'

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('rounded-3xl', className)} {...props} />
))
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
))
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('border-grey-light border-t font-medium', className)}
    {...props}
  />
))
TableFooter.displayName = 'TableFooter'

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => {
    const childrenArray = React.Children.toArray(props.children)

    return (
      <tr
        ref={ref}
        className={cn(
          'transition-colors border-b border-white-active h-16 text-sm font-light',
          className
        )}
        {...props}
      >
        {childrenArray.map((child, index) =>
          React.cloneElement(child as React.ReactElement, {
            key: index,
            isfirst: `${index === 0}`,
            islast: `${index === childrenArray.length - 1}`
          })
        )}
      </tr>
    )
  }
)
TableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'text-black bg-primary-light pl-4 pr-1 text-left align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    style={typography['secondary-body']}
    {...props}
  />
))
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    isfirst?: 'true' | 'false'
    islast?: 'true' | 'false'
  }
>(({ className, children, isfirst: _isfirst, islast: _islast, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'pl-4 pr-1 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...props}
  >
    {children}
  </td>
))
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('text-muted-foreground mt-4 text-sm', className)} {...props} />
))
TableCaption.displayName = 'TableCaption'

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption }
